import { useQuery } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import BackstopIcon from "@/assets/Icons/backstop.png";

// Connector key -> brand icon. Only connectors with a distinct icon appear;
// anything else falls back to the generic read-only-drive icon.
const CONNECTOR_ICONS: Record<string, string> = {
  backstop: BackstopIcon,
};

// Which of the current org's drives render read-only in the file tree, and
// which of those are SFTP-backed and can be re-synced on demand (#10860).
//
// The backend resolves this per-org (#10972): the names of client drives, and
// the organization IDs some of them are scoped to, must not appear in the
// frontend bundle — it is served unauthenticated to anyone who loads the app.
export interface DrivePolicy {
  // Lowercased, so callers compare against a lowercased folder name.
  read_only_drive_names: string[];
  sftp_drive_names: string[];
  // Drive name -> the connector backing it. Connector keys name third-party
  // products (Backstop, SFTP), so mapping them to an icon in the frontend
  // reveals nothing about who the client is.
  drive_connectors: Record<string, string>;
  // Lowercased names of the org's named SharePoint site roots (#11713): each
  // connected site syncs into its own top-level folder, which renders with
  // the SharePoint icon and read-only treatment. Optional for deploy skew —
  // the frontend can ship ahead of the backend serving the field.
  sharepoint_root_names?: string[];
}

export const DRIVE_POLICY_QUERY_KEY = ["users", "drive-policy"] as const;

const EMPTY_POLICY: DrivePolicy = {
  read_only_drive_names: [],
  sftp_drive_names: [],
  drive_connectors: {},
  sharepoint_root_names: [],
};

const fetchDrivePolicy = async (): Promise<DrivePolicy> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/users/drive-policy`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch drive policy: ${response.status}`);
  }
  return response.json();
};

// The connector icon to show on a read-only drive's root folder, or null for
// the generic read-only icon.
export const driveConnectorIcon = (
  driveName: string | undefined,
  driveConnectors?: Record<string, string>
): string | null => {
  if (!driveName || !driveConnectors) return null;
  const connector = driveConnectors[driveName.toLowerCase()];
  return connector ? (CONNECTOR_ICONS[connector] ?? null) : null;
};

// Both file trees read this, and it changes about never, so it is cached for
// the session rather than refetched per mount.
export const useDrivePolicy = (): DrivePolicy => {
  const { data } = useQuery({
    queryKey: DRIVE_POLICY_QUERY_KEY,
    queryFn: fetchDrivePolicy,
    staleTime: Infinity,
  });
  // Before it resolves, no folder is a read-only client drive. That only
  // affects presentation — pin-to-top, icon, and hiding edit affordances — and
  // the server still refuses writes it wouldn't have allowed anyway.
  return data ?? EMPTY_POLICY;
};
